import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { AlertModal } from '../components/AlertModal';
import { BlueskyIcon } from '../components/BlueskyIcon';
import { ConfirmModal } from '../components/ConfirmModal';
import { ImageLightbox } from '../components/ImageLightbox';
import { RichText } from '../components/RichText';
import {
  apiClient,
  type Embed,
  type Facet,
  isEmbedExternal,
  isEmbedImages,
  isEmbedVideo,
  type ThreadgateRule
} from '../lib/api-client';
import { formatDateTime } from '../utils/datetime';
import { generateImageUrl, type ImageData } from '../utils/image-url';

interface Post {
  id: string;
  user_id: string;
  content: string;
  scheduled_at: string;
  status: string;
  created_at: string;
  updated_at: string;
  executed_at: string | null;
  at_uri: string | null;
  at_rkey: string | null;
  error_msg: string | null;
  languages: string[] | null;
  facets: Facet[] | null;
  embed: Embed | null;
  threadgate_rules: ThreadgateRule[] | null;
  threadgate_uri: string | null;
  threadgate_cid: string | null;
  postgate_disable_embedding: boolean | null;
  postgate_uri: string | null;
  postgate_cid: string | null;
  content_labels: {
    $type: 'com.atproto.label.defs#selfLabels';
    values: Array<{ val: string }>;
  } | null;
}

// Helper function to decode facet text safely
const decodeFacetText = (content: string, facet: Facet): string => {
  if (facet?.index?.byteStart == null || facet?.index?.byteEnd == null) {
    return '';
  }
  try {
    const bytes = new TextEncoder().encode(content);
    const extracted = bytes.slice(facet.index.byteStart, facet.index.byteEnd);
    // fatal: true option for strict byte sequence checking
    return new TextDecoder('utf-8', { fatal: true }).decode(extracted);
  } catch (e) {
    console.error('Failed to decode facet text:', e);
    return '⚠️ Text Decode Error';
  }
};

// ThreadGate rule display name mapping
const THREADGATE_RULE_NAMES: Record<string, string> = {
  mention: 'Mentioned Users Only',
  follower: 'Followers Only',
  following: 'Following Only'
};

// AT Protocol lexicon $type → legacy rule key mapping
const THREADGATE_RULE_TYPE_TO_KEY: Record<string, string> = {
  'app.bsky.feed.threadgate#mentionRule': 'mention',
  'app.bsky.feed.threadgate#followerRule': 'follower',
  'app.bsky.feed.threadgate#followingRule': 'following'
};

// DB には旧形式（string）と lexicon 形式（{ $type }）が混在するため両対応で表示名に変換する
const threadgateRuleLabel = (rule: ThreadgateRule): string => {
  const key =
    typeof rule === 'string' ? rule : (THREADGATE_RULE_TYPE_TO_KEY[rule?.$type] ?? rule?.$type);
  return THREADGATE_RULE_NAMES[key] ?? key ?? 'unknown';
};

// Helper function to check if URI is safe (http/https only)
const isSafeUri = (uri: string): boolean => {
  return uri.startsWith('http://') || uri.startsWith('https://');
};

// Helper function to safely extract hostname from URI
const safeHostname = (uri: string): string => {
  try {
    if (!isSafeUri(uri)) {
      return 'Invalid URL';
    }
    return new URL(uri).hostname;
  } catch {
    return 'Invalid URL';
  }
};

// Helper function to extract CID $link from blob ref
const getBlobCid = (ref: unknown): string => {
  if (ref && typeof ref === 'object' && '$link' in ref) {
    return (ref as { $link: string }).$link;
  }
  return String(ref);
};

interface User {
  id: string;
  did: string;
  handle: string;
  display_name: string | null;
  avatar: string | null;
}

export default function PostDetailPage() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<Post | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [revealMedia, setRevealMedia] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'default' | 'danger';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    variant?: 'info' | 'success' | 'warning' | 'error';
  }>({
    isOpen: false,
    title: '',
    message: ''
  });
  const [lightbox, setLightbox] = useState<{
    isOpen: boolean;
    images: Array<{ url: string; alt?: string }>;
    initialIndex: number;
  }>({
    isOpen: false,
    images: [],
    initialIndex: 0
  });

  // Thread child posts state
  const [threadPosts, setThreadPosts] = useState<
    Array<{
      id: string;
      user_id: string;
      content: string;
      scheduled_at: string;
      status: string;
      executed_at: string | null;
      error_msg: string | null;
      languages: string[] | null;
      facets: Facet[] | null;
      embed: Embed | null;
      parent_post_id: string | null;
    }>
  >([]);

  // Video playback state
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [threadVideoPlayingMap, setThreadVideoPlayingMap] = useState<Map<string, boolean>>(
    new Map()
  );

  // Content warning logic
  const hasContentWarning = useMemo(() => {
    return (post?.content_labels?.values?.length ?? 0) > 0;
  }, [post?.content_labels]);

  // Memoize lightbox images to avoid regenerating on each click
  const lightboxImages = useMemo(() => {
    if (!isEmbedImages(post?.embed)) {
      return [];
    }
    return post.embed.images.map(img => ({
      url: generateImageUrl({
        ...(img.image ?? img),
        key: (img as { key?: string }).key,
        actorDid: user?.did ?? post.user_id
      } as unknown as ImageData),
      alt: img.alt
    }));
  }, [post?.embed, user?.did, post?.user_id]);

  useEffect(() => {
    loadPostDetail();
  }, [postId]);

  const loadPostDetail = async () => {
    try {
      setLoading(true);
      setError('');

      // Get post details
      const postResponse = await apiClient.getPost(postId!);
      setPost(postResponse);

      // Get user details and thread posts in parallel
      // スレッドはセカンダリ要素のため、取得失敗時もメインの投稿詳細は表示する
      const [userResponse, threadResponse] = await Promise.all([
        postResponse.user_id ? apiClient.getUser(postResponse.user_id) : Promise.resolve(null),
        apiClient.getPostThread(postId!).catch(err => {
          console.error('Failed to load thread posts:', err);
          return { posts: [] };
        })
      ]);

      if (userResponse) {
        setUser(userResponse);
      }
      setThreadPosts(threadResponse.posts);
    } catch (err) {
      setError('Failed to load post details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = (newStatus: string) => {
    if (!post) return;

    setConfirmModal({
      isOpen: true,
      title: 'Update Post Status',
      message: `Change post status from ${post.status} to ${newStatus}?`,
      variant: 'default',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        try {
          setUpdating(true);
          setError('');

          // Update post status via PATCH
          await apiClient.updatePost(post.id, { status: newStatus });

          setAlertModal({
            isOpen: true,
            title: 'Success',
            message: `Status changed to ${newStatus}`,
            variant: 'success'
          });
          await loadPostDetail();
        } catch (err) {
          setError(`Failed to update status: ${err}`);
          console.error(err);
        } finally {
          setUpdating(false);
        }
      }
    });
  };

  const deletePost = () => {
    if (!post) return;

    setConfirmModal({
      isOpen: true,
      title: 'Delete Post',
      message: 'Are you sure you want to delete this post? This action cannot be undone.',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        try {
          setUpdating(true);
          await apiClient.deletePost(post.id);

          setAlertModal({
            isOpen: true,
            title: 'Success',
            message: 'Post deleted successfully',
            variant: 'success'
          });
          // Navigate after a brief delay to show the alert
          setTimeout(() => navigate('/reserved-actions'), 1500);
        } catch (err) {
          setError(`Failed to delete post: ${err}`);
          console.error(err);
          setUpdating(false);
        }
      }
    });
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center p-8'>
        <div className='text-lg text-gray-900 dark:text-gray-100'>Loading...</div>
      </div>
    );
  }

  if (error && !post) {
    return (
      <div className='p-8'>
        <div className='rounded-lg bg-red-50 p-4 text-red-600 dark:bg-red-950 dark:text-red-400'>
          {error}
        </div>
        <Link
          to='/reserved-actions'
          className='mt-4 inline-block text-blue-600 hover:underline dark:text-blue-400 dark:hover:text-blue-300'
        >
          ← Back to Posts
        </Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className='p-8'>
        <div className='text-gray-600 dark:text-gray-400'>Post not found</div>
        <Link
          to='/reserved-actions'
          className='mt-4 inline-block text-blue-600 hover:underline dark:text-blue-400 dark:hover:text-blue-300'
        >
          ← Back to Posts
        </Link>
      </div>
    );
  }

  const canChangeStatus = ['PENDING', 'PROCESSING', 'CANCELLED'].includes(post.status);
  const availableStatuses = ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED', 'CANCELLED'].filter(
    s => s !== post.status
  );

  return (
    <div className='p-8'>
      <div className='mb-6 flex items-center justify-between'>
        <h1 className='text-3xl font-bold text-gray-800 dark:text-white'>Post Details</h1>
        <Link
          to='/reserved-actions'
          className='text-blue-600 hover:underline dark:text-blue-400 dark:hover:text-blue-300'
        >
          ← Back to Posts
        </Link>
      </div>

      {error && (
        <div className='mb-4 rounded-lg bg-red-50 p-4 text-red-600 dark:bg-red-950 dark:text-red-400'>
          {error}
        </div>
      )}

      {/* Post Preview */}
      <div className='mb-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800 dark:shadow-gray-900'>
        <h2 className='mb-4 text-xl font-semibold text-gray-900 dark:text-gray-100'>
          Post Preview
        </h2>

        <div className='flex flex-wrap items-center justify-between gap-4'>
          {user && (
            <div className='mb-4 flex items-center gap-3'>
              {user.avatar && (
                <img
                  src={user.avatar}
                  alt={user.display_name || user.handle}
                  className='h-12 w-12 rounded-full'
                />
              )}
              <div>
                <div className='font-semibold text-gray-900 dark:text-gray-100'>
                  {user.display_name || user.handle}
                </div>
                <div className='flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400'>
                  <span>@{user.handle}</span>
                  <a
                    href={`https://bsky.app/profile/${user.handle}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center gap-1 rounded-md bg-blue-500 px-2 py-0.5 text-xs font-medium text-white transition-colors hover:bg-blue-600'
                    title='View on Bluesky'
                  >
                    <BlueskyIcon className='h-3 w-3' />
                    Bluesky
                  </a>
                </div>
              </div>
            </div>
          )}

          {hasContentWarning && (
            <div className='mb-4 flex flex-wrap items-center gap-2'>
              <span className='rounded bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800 dark:bg-amber-900/40 dark:text-amber-400'>
                ⚠️ CONTENT WARNING
              </span>
              {post.content_labels?.values.map((label, idx) => (
                <span
                  key={idx}
                  className='rounded bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800 dark:bg-orange-900/50 dark:text-orange-300'
                >
                  {label.val}
                </span>
              ))}
              <button
                type='button'
                onClick={() => setRevealMedia(!revealMedia)}
                className='text-xs font-medium text-blue-600 hover:underline dark:text-blue-400'
              >
                {revealMedia ? 'Blur media' : 'Show media'}
              </button>
            </div>
          )}
        </div>

        <div className='rounded-lg bg-gray-50 p-4 dark:bg-gray-700'>
          <RichText content={post.content} facets={post.facets as any} />
        </div>

        {/* Embed Images */}
        {isEmbedImages(post.embed) && (
          <div className='mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2'>
            {post.embed.images.map((image, index) => {
              const imageUrl = generateImageUrl({
                ...(image.image ?? image),
                key: (image as { key?: string }).key,
                actorDid: user?.did ?? post.user_id
              } as unknown as ImageData);
              return (
                <div
                  key={index}
                  className='group relative aspect-video cursor-pointer overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-700'
                  onClick={() => {
                    setLightbox({ isOpen: true, images: lightboxImages, initialIndex: index });
                  }}
                >
                  <img
                    src={imageUrl}
                    alt={image.alt ?? `Image ${index + 1}`}
                    className={`h-full w-full object-cover transition-transform group-hover:scale-105 ${
                      hasContentWarning && !revealMedia ? 'scale-110 blur-2xl' : ''
                    }`}
                    loading='lazy'
                  />
                  {hasContentWarning && !revealMedia && (
                    <div className='absolute inset-0 flex items-center justify-center bg-black/10'>
                      <span className='rounded-full bg-black/50 px-3 py-1 text-xs font-bold text-white backdrop-blur-sm'>
                        Sensitive Content
                      </span>
                    </div>
                  )}
                  <div className='absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/10' />
                  {image.alt && (
                    <div className='bg-opacity-60 dark:bg-opacity-70 absolute right-0 bottom-0 left-0 bg-black p-2 text-xs text-white'>
                      {image.alt}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Embed Video */}
        {isEmbedVideo(post.embed) &&
          (() => {
            const videoKey = post.embed.video.key ?? post.embed.video.ref?.$link;
            const isStorageKey =
              videoKey?.startsWith('temp/') || videoKey?.startsWith('usercontent/');

            let videoUrl = '';
            let thumbnailUrl = '';

            // 投稿成功後は CID が入るため、Bluesky CDN の URL を生成する
            // 投稿前（ストレージキー）は R2 の URL を生成する
            const actorDid = user?.did ?? post.user_id;

            if (videoKey && isStorageKey) {
              // R2 ストレージキー → R2 URL
              videoUrl = generateImageUrl({
                key: videoKey,
                actorDid
              } as unknown as ImageData);
              thumbnailUrl = generateImageUrl({
                key: `${videoKey}_thumb`,
                actorDid
              } as unknown as ImageData);
            } else if (videoKey) {
              // CID → Bluesky CDN URL (投稿成功後)
              videoUrl = `https://video.cdn.bsky.app/hls/${actorDid}/${videoKey}/playlist.m3u8`;
              thumbnailUrl = `https://video.cdn.bsky.app/hls/${actorDid}/${videoKey}/thumbnail.jpg`;
            }

            return (
              <div className='mt-3'>
                <div className='relative aspect-video overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-700'>
                  {videoPlaying ? (
                    <video
                      src={videoUrl}
                      controls
                      autoPlay
                      className='h-full w-full object-contain'
                    >
                      <track kind='captions' />
                    </video>
                  ) : (
                    <button
                      type='button'
                      className='group relative h-full w-full cursor-pointer'
                      onClick={() => setVideoPlaying(true)}
                    >
                      {thumbnailUrl ? (
                        <img
                          src={thumbnailUrl}
                          alt={post.embed.alt ?? 'Video thumbnail'}
                          className={`h-full w-full object-cover ${
                            hasContentWarning && !revealMedia ? 'scale-110 blur-2xl' : ''
                          }`}
                          loading='lazy'
                        />
                      ) : (
                        <div className='flex h-full w-full items-center justify-center bg-gray-200 dark:bg-gray-600'>
                          <svg
                            className='h-12 w-12 text-gray-400'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth='2'
                              d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
                            />
                          </svg>
                        </div>
                      )}
                      {/* Play button overlay */}
                      <div className='absolute inset-0 flex items-center justify-center bg-black/0 transition-all duration-200 group-hover:bg-black/20'>
                        <div className='flex h-16 w-16 items-center justify-center rounded-full bg-black/60 text-white transition-transform duration-200 group-hover:scale-110'>
                          <svg className='ml-1 h-8 w-8' fill='currentColor' viewBox='0 0 24 24'>
                            <path d='M8 5v14l11-7z' />
                          </svg>
                        </div>
                      </div>
                    </button>
                  )}
                </div>
                {post.embed.alt && (
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>{post.embed.alt}</p>
                )}
              </div>
            );
          })()}

        {/* Embed External (Link Card) */}
        {isEmbedExternal(post.embed) && (
          <div className='mt-3'>
            <a
              href={isSafeUri(post.embed.external.uri) ? post.embed.external.uri : '#'}
              target='_blank'
              rel='noopener noreferrer'
              className='block overflow-hidden rounded-xl border border-gray-200 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700'
            >
              {/* thumb の blob 参照解決には actorDid が必須のため、user.did ロード完了まで待機 */}
              {((post.embed.external.thumb && user?.did) ?? post.embed.external.thumbUrl) && (
                <img
                  src={
                    post.embed.external.thumb && user?.did
                      ? generateImageUrl({
                          ...post.embed.external.thumb,
                          actorDid: user.did
                        } as unknown as ImageData)
                      : generateImageUrl({
                          thumbUrl: post.embed.external.thumbUrl
                        } as unknown as ImageData)
                  }
                  alt={post.embed.external.title ?? 'Link preview'}
                  className='aspect-video w-full object-cover'
                  loading='lazy'
                  onError={() => {
                    if (isEmbedExternal(post.embed)) {
                      console.error(
                        '[PostDetail] Failed to load external thumb:',
                        post.embed.external.thumb ?? post.embed.external.thumbUrl
                      );
                    }
                  }}
                />
              )}
              <div className='p-3'>
                {post.embed.external.title && (
                  <div className='font-semibold text-gray-900 dark:text-gray-100'>
                    {post.embed.external.title}
                  </div>
                )}
                {post.embed.external.description && (
                  <div className='mt-1 text-sm text-gray-600 dark:text-gray-400'>
                    {post.embed.external.description}
                  </div>
                )}
                <div className='mt-1 text-xs text-gray-500 dark:text-gray-500'>
                  {safeHostname(post.embed.external.uri)}
                </div>
              </div>
            </a>
          </div>
        )}

        {post.languages && post.languages.length > 0 && (
          <div className='mt-4'>
            <span className='text-sm text-gray-600 dark:text-gray-400'>Languages: </span>
            <span className='text-sm text-gray-900 dark:text-gray-100'>
              {post.languages.join(', ')}
            </span>
          </div>
        )}
      </div>

      {/* Thread Posts */}
      {threadPosts.length > 0 && (
        <div className='mb-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800 dark:shadow-gray-900'>
          <h2 className='mb-4 text-xl font-semibold text-gray-900 dark:text-gray-100'>
            Thread ({threadPosts.length} {threadPosts.length === 1 ? 'post' : 'posts'})
          </h2>
          <div className='space-y-6'>
            {threadPosts.map((threadPost, index) => {
              const threadLightboxImages = isEmbedImages(threadPost.embed)
                ? threadPost.embed.images.map(img => ({
                    url: generateImageUrl({
                      ...(img.image ?? img),
                      key: (img as { key?: string }).key,
                      actorDid: user?.did ?? threadPost.user_id
                    } as unknown as ImageData),
                    alt: img.alt
                  }))
                : [];

              return (
                <div
                  key={threadPost.id}
                  className='relative border-l-2 border-purple-300 pl-4 dark:border-purple-600'
                >
                  {/* Header */}
                  <div className='mb-2 flex items-center gap-2'>
                    <span className='text-xs font-semibold text-purple-600 dark:text-purple-400'>
                      #{index + 1}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        threadPost.status === 'PENDING'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : threadPost.status === 'COMPLETED'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : threadPost.status === 'FAILED'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}
                    >
                      {threadPost.status}
                    </span>
                  </div>

                  {/* Rich Text Preview */}
                  <div className='rounded-lg bg-gray-50 p-3 dark:bg-gray-700'>
                    <RichText content={threadPost.content} facets={threadPost.facets as any} />
                  </div>

                  {/* Embed Images */}
                  {isEmbedImages(threadPost.embed) && (
                    <div className='mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2'>
                      {threadPost.embed.images.map((image, imgIndex) => {
                        const imageUrl = generateImageUrl({
                          ...(image.image ?? image),
                          key: (image as { key?: string }).key,
                          actorDid: user?.did ?? threadPost.user_id
                        } as unknown as ImageData);
                        return (
                          <div
                            key={imgIndex}
                            className='group relative aspect-video cursor-pointer overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-700'
                            onClick={() => {
                              setLightbox({
                                isOpen: true,
                                images: threadLightboxImages,
                                initialIndex: imgIndex
                              });
                            }}
                          >
                            <img
                              src={imageUrl}
                              alt={image.alt ?? `Image ${imgIndex + 1}`}
                              className='h-full w-full object-cover transition-transform group-hover:scale-105'
                              loading='lazy'
                            />
                            <div className='absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/10' />
                            {image.alt && (
                              <div className='bg-opacity-60 dark:bg-opacity-70 absolute right-0 bottom-0 left-0 bg-black p-2 text-xs text-white'>
                                {image.alt}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Embed Video */}
                  {isEmbedVideo(threadPost.embed) &&
                    (() => {
                      const videoKey =
                        threadPost.embed.video.key ?? threadPost.embed.video.ref?.$link;
                      const isStorageKey =
                        videoKey?.startsWith('temp/') ?? videoKey?.startsWith('usercontent/');
                      const actorDid = user?.did ?? threadPost.user_id;
                      let videoUrl = '';
                      let thumbnailUrl = '';

                      if (videoKey && isStorageKey) {
                        videoUrl = generateImageUrl({ key: videoKey, actorDid } as unknown as ImageData);
                        thumbnailUrl = generateImageUrl({
                          key: `${videoKey}_thumb`,
                          actorDid
                        } as unknown as ImageData);
                      } else if (videoKey) {
                        videoUrl = `https://video.cdn.bsky.app/hls/${actorDid}/${videoKey}/playlist.m3u8`;
                        thumbnailUrl = `https://video.cdn.bsky.app/hls/${actorDid}/${videoKey}/thumbnail.jpg`;
                      }

                      const isPlaying = threadVideoPlayingMap.get(threadPost.id) ?? false;

                      return (
                        <div className='mt-2'>
                          <div className='relative aspect-video overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-700'>
                            {isPlaying ? (
                              <video
                                src={videoUrl}
                                controls
                                autoPlay
                                className='h-full w-full object-contain'
                              >
                                <track kind='captions' />
                              </video>
                            ) : (
                              <button
                                type='button'
                                className='group relative h-full w-full cursor-pointer'
                                onClick={() => {
                                  setThreadVideoPlayingMap(prev => {
                                    const next = new Map(prev);
                                    next.set(threadPost.id, true);
                                    return next;
                                  });
                                }}
                              >
                                {thumbnailUrl ? (
                                  <img
                                    src={thumbnailUrl}
                                    alt={threadPost.embed.alt ?? 'Video thumbnail'}
                                    className='h-full w-full object-cover'
                                    loading='lazy'
                                  />
                                ) : (
                                  <div className='flex h-full w-full items-center justify-center bg-gray-200 dark:bg-gray-600'>
                                    <svg
                                      className='h-12 w-12 text-gray-400'
                                      fill='none'
                                      stroke='currentColor'
                                      viewBox='0 0 24 24'
                                    >
                                      <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
                                      />
                                    </svg>
                                  </div>
                                )}
                                <div className='absolute inset-0 flex items-center justify-center bg-black/0 transition-all duration-200 group-hover:bg-black/20'>
                                  <div className='flex h-16 w-16 items-center justify-center rounded-full bg-black/60 text-white transition-transform duration-200 group-hover:scale-110'>
                                    <svg
                                      className='ml-1 h-8 w-8'
                                      fill='currentColor'
                                      viewBox='0 0 24 24'
                                    >
                                      <path d='M8 5v14l11-7z' />
                                    </svg>
                                  </div>
                                </div>
                              </button>
                            )}
                          </div>
                          {threadPost.embed.alt && (
                            <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                              {threadPost.embed.alt}
                            </p>
                          )}
                        </div>
                      );
                    })()}

                  {/* Embed External (Link Card) */}
                  {isEmbedExternal(threadPost.embed) && (
                    <div className='mt-2'>
                      <a
                        href={
                          isSafeUri(threadPost.embed.external.uri)
                            ? threadPost.embed.external.uri
                            : '#'
                        }
                        target='_blank'
                        rel='noopener noreferrer'
                        className='block overflow-hidden rounded-xl border border-gray-200 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700'
                      >
                        {((threadPost.embed.external.thumb && user?.did) ??
                          threadPost.embed.external.thumbUrl) && (
                          <img
                            src={
                              threadPost.embed.external.thumb && user?.did
                                ? generateImageUrl({
                                    ...threadPost.embed.external.thumb,
                                    actorDid: user.did
                                  } as unknown as ImageData)
                                : generateImageUrl({
                                    thumbUrl: threadPost.embed.external.thumbUrl
                                  } as unknown as ImageData)
                            }
                            alt={threadPost.embed.external.title ?? 'Link preview'}
                            className='aspect-video w-full object-cover'
                            loading='lazy'
                          />
                        )}
                        <div className='p-3'>
                          {threadPost.embed.external.title && (
                            <div className='font-semibold text-gray-900 dark:text-gray-100'>
                              {threadPost.embed.external.title}
                            </div>
                          )}
                          {threadPost.embed.external.description && (
                            <div className='mt-1 text-sm text-gray-600 dark:text-gray-400'>
                              {threadPost.embed.external.description}
                            </div>
                          )}
                          <div className='mt-1 text-xs text-gray-500 dark:text-gray-500'>
                            {safeHostname(threadPost.embed.external.uri)}
                          </div>
                        </div>
                      </a>
                    </div>
                  )}

                  {/* Error */}
                  {threadPost.error_msg && (
                    <p className='mt-2 text-xs text-red-600 dark:text-red-400'>
                      Error: {threadPost.error_msg}
                    </p>
                  )}

                  {/* Raw Content */}
                  <details className='mt-3'>
                    <summary className='cursor-pointer text-sm text-blue-600 hover:underline dark:text-blue-400 dark:hover:text-blue-300'>
                      Raw Content
                    </summary>
                    <div className='mt-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-700'>
                      <pre className='font-mono text-sm break-words whitespace-pre-wrap text-gray-900 dark:text-gray-100'>
                        {threadPost.content}
                      </pre>
                    </div>
                    <div className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                      Length: {threadPost.content.length} characters | Byte length:{' '}
                      {new Blob([threadPost.content]).size} bytes
                    </div>
                  </details>

                  {/* Facets Details */}
                  {Array.isArray(threadPost.facets) &&
                    (threadPost.facets as any[]).length > 0 && (
                      <details className='mt-2'>
                        <summary className='cursor-pointer text-sm text-blue-600 hover:underline dark:text-blue-400 dark:hover:text-blue-300'>
                          Facets Details ({(threadPost.facets as any[]).length})
                        </summary>
                        <div className='mt-2 space-y-3'>
                          {(threadPost.facets as any[]).map((facet: any, fIdx: number) => (
                            <div
                              key={fIdx}
                              className='rounded-lg border border-gray-200 p-3 dark:border-gray-600'
                            >
                              <div className='mb-2 flex items-center justify-between'>
                                <span className='text-sm font-semibold text-gray-900 dark:text-gray-100'>
                                  Facet #{fIdx + 1}
                                </span>
                                <span className='rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200'>
                                  {facet.$type}
                                </span>
                              </div>
                              <div className='mb-2 grid grid-cols-2 gap-2 text-sm'>
                                <div>
                                  <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                    Byte Start:
                                  </span>{' '}
                                  <span className='text-gray-900 dark:text-gray-100'>
                                    {facet.index?.byteStart}
                                  </span>
                                </div>
                                <div>
                                  <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                    Byte End:
                                  </span>{' '}
                                  <span className='text-gray-900 dark:text-gray-100'>
                                    {facet.index?.byteEnd}
                                  </span>
                                </div>
                              </div>
                              {facet.index?.byteStart != null && facet.index?.byteEnd != null && (
                                <div className='mb-2'>
                                  <span className='text-sm font-semibold text-gray-600 dark:text-gray-400'>
                                    Extracted Text:
                                  </span>
                                  <div className='mt-1 rounded bg-yellow-50 px-3 py-2 dark:bg-yellow-900/20'>
                                    <code className='text-sm text-gray-900 dark:text-gray-100'>
                                      {decodeFacetText(threadPost.content, facet)}
                                    </code>
                                  </div>
                                </div>
                              )}
                              {facet.features &&
                                Array.isArray(facet.features) &&
                                facet.features.length > 0 && (
                                  <div>
                                    <h4 className='mb-1 text-sm font-semibold text-gray-600 dark:text-gray-400'>
                                      Features ({facet.features.length}):
                                    </h4>
                                    <div className='space-y-2'>
                                      {facet.features.map((feature: any, fiIdx: number) => (
                                        <div
                                          key={fiIdx}
                                          className='rounded bg-gray-50 p-2 dark:bg-gray-700'
                                        >
                                          <div className='mb-1'>
                                            <span className='rounded bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800 dark:bg-purple-900 dark:text-purple-200'>
                                              {feature.$type}
                                            </span>
                                          </div>
                                          {feature.$type ===
                                            'app.bsky.richtext.facet#mention' && (
                                            <div className='text-sm'>
                                              <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                                DID:
                                              </span>{' '}
                                              <code
                                                className={`rounded px-1 ${
                                                  feature.did?.startsWith('did:')
                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                                }`}
                                              >
                                                {feature.did}
                                              </code>
                                            </div>
                                          )}
                                          {feature.$type === 'app.bsky.richtext.facet#link' && (
                                            <div className='text-sm'>
                                              <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                                URI:
                                              </span>{' '}
                                              <a
                                                href={feature.uri}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                className='text-blue-600 hover:underline dark:text-blue-400'
                                              >
                                                {feature.uri}
                                              </a>
                                            </div>
                                          )}
                                          {feature.$type === 'app.bsky.richtext.facet#tag' && (
                                            <div className='text-sm'>
                                              <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                                Tag:
                                              </span>{' '}
                                              <code className='rounded bg-blue-100 px-1 text-blue-800 dark:bg-blue-900 dark:text-blue-200'>
                                                #{feature.tag}
                                              </code>
                                            </div>
                                          )}
                                          {![
                                            'app.bsky.richtext.facet#mention',
                                            'app.bsky.richtext.facet#link',
                                            'app.bsky.richtext.facet#tag'
                                          ].includes(feature.$type) && (
                                            <div className='text-sm'>
                                              <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                                Raw Data:
                                              </span>
                                              <pre className='mt-1 overflow-auto text-xs text-gray-900 dark:text-gray-100'>
                                                {JSON.stringify(feature, null, 2)}
                                              </pre>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                        <details className='mt-2'>
                          <summary className='cursor-pointer text-xs text-blue-600 hover:underline dark:text-blue-400'>
                            Show Raw JSON
                          </summary>
                          <pre className='mt-2 max-h-60 overflow-auto rounded bg-gray-50 p-3 text-xs text-gray-900 dark:bg-gray-700 dark:text-gray-100'>
                            {JSON.stringify(threadPost.facets, null, 2)}
                          </pre>
                        </details>
                      </details>
                    )}

                  {/* Embed Details */}
                  {threadPost.embed != null && (
                    <details className='mt-2'>
                      <summary className='cursor-pointer text-sm text-blue-600 hover:underline dark:text-blue-400 dark:hover:text-blue-300'>
                        Embed Details
                      </summary>
                      <div className='mt-2 rounded-lg border border-gray-200 p-3 dark:border-gray-600'>
                        <div className='mb-2'>
                          <span className='rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200'>
                            {isEmbedImages(threadPost.embed)
                              ? 'app.bsky.embed.images'
                              : isEmbedExternal(threadPost.embed)
                                ? 'app.bsky.embed.external'
                                : isEmbedVideo(threadPost.embed)
                                  ? 'app.bsky.embed.video'
                                  : 'unknown'}
                          </span>
                        </div>

                        {isEmbedImages(threadPost.embed) && (
                          <div>
                            <h4 className='mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100'>
                              Images ({threadPost.embed.images.length})
                            </h4>
                            <div className='space-y-2'>
                              {threadPost.embed.images.map((image, imgIdx) => (
                                <div
                                  key={imgIdx}
                                  className='rounded bg-gray-50 p-2 text-sm dark:bg-gray-700'
                                >
                                  <div className='mb-1 font-semibold text-gray-900 dark:text-gray-100'>
                                    Image #{imgIdx + 1}
                                  </div>
                                  {image.image?.ref && (
                                    <div>
                                      <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                        CID:
                                      </span>{' '}
                                      <code className='break-all text-gray-900 dark:text-gray-100'>
                                        {getBlobCid(image.image.ref)}
                                      </code>
                                    </div>
                                  )}
                                  {image.image?.mimeType && (
                                    <div>
                                      <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                        MIME Type:
                                      </span>{' '}
                                      <span className='text-gray-900 dark:text-gray-100'>
                                        {image.image.mimeType}
                                      </span>
                                    </div>
                                  )}
                                  {image.image?.size != null && (
                                    <div>
                                      <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                        Size:
                                      </span>{' '}
                                      <span className='text-gray-900 dark:text-gray-100'>
                                        {(image.image.size / 1024).toFixed(2)} KB
                                      </span>
                                    </div>
                                  )}
                                  {image.alt && (
                                    <div>
                                      <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                        Alt Text:
                                      </span>
                                      <div className='mt-1 rounded bg-yellow-50 p-1 dark:bg-yellow-900/20'>
                                        <span className='text-gray-900 dark:text-gray-100'>
                                          {image.alt}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                  {image.aspectRatio && (
                                    <div>
                                      <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                        Aspect Ratio:
                                      </span>{' '}
                                      <span className='text-gray-900 dark:text-gray-100'>
                                        {image.aspectRatio.width} : {image.aspectRatio.height}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {isEmbedExternal(threadPost.embed) && (
                          <div>
                            <h4 className='mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100'>
                              External Link
                            </h4>
                            <div className='space-y-1 text-sm'>
                              <div>
                                <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                  URI:
                                </span>{' '}
                                <a
                                  href={
                                    isSafeUri(threadPost.embed.external.uri)
                                      ? threadPost.embed.external.uri
                                      : '#'
                                  }
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='text-blue-600 hover:underline dark:text-blue-400'
                                >
                                  {threadPost.embed.external.uri}
                                </a>
                              </div>
                              {threadPost.embed.external.title && (
                                <div>
                                  <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                    Title:
                                  </span>{' '}
                                  <span className='text-gray-900 dark:text-gray-100'>
                                    {threadPost.embed.external.title}
                                  </span>
                                </div>
                              )}
                              {threadPost.embed.external.description && (
                                <div>
                                  <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                    Description:
                                  </span>
                                  <div className='mt-1 text-gray-900 dark:text-gray-100'>
                                    {threadPost.embed.external.description}
                                  </div>
                                </div>
                              )}
                              {threadPost.embed.external.thumb?.ref && (
                                <div>
                                  <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                    Thumbnail CID:
                                  </span>{' '}
                                  <code className='text-xs break-all text-gray-900 dark:text-gray-100'>
                                    {getBlobCid(threadPost.embed.external.thumb.ref)}
                                  </code>
                                </div>
                              )}
                              {threadPost.embed.external.thumb?.mimeType && (
                                <div className='text-xs text-gray-600 dark:text-gray-400'>
                                  MIME: {threadPost.embed.external.thumb.mimeType}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <details className='mt-3'>
                          <summary className='cursor-pointer text-xs text-blue-600 hover:underline dark:text-blue-400'>
                            Show Raw JSON
                          </summary>
                          <pre className='mt-2 max-h-60 overflow-auto rounded bg-gray-50 p-3 text-xs text-gray-900 dark:bg-gray-700 dark:text-gray-100'>
                            {JSON.stringify(threadPost.embed, null, 2)}
                          </pre>
                        </details>
                      </div>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Post Details */}
      <div className='mb-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800 dark:shadow-gray-900'>
        <h2 className='mb-4 text-xl font-semibold text-gray-900 dark:text-gray-100'>
          Post Information
        </h2>

        <dl className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <div>
            <dt className='font-semibold text-gray-600 dark:text-gray-400'>Status</dt>
            <dd>
              <span
                className={`inline-block rounded px-2 py-1 text-sm font-semibold ${
                  post.status === 'PENDING'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    : post.status === 'PROCESSING'
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                      : post.status === 'COMPLETED'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : post.status === 'FAILED'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}
              >
                {post.status}
              </span>
            </dd>
          </div>

          <div>
            <dt className='font-semibold text-gray-600 dark:text-gray-400'>Content Labels</dt>
            <dd>
              {hasContentWarning ? (
                <div className='flex flex-wrap gap-1'>
                  {post.content_labels?.values.map((label, idx) => (
                    <span
                      key={idx}
                      className='inline-block rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                    >
                      {label.val}
                    </span>
                  ))}
                </div>
              ) : (
                <span className='text-sm text-gray-500 dark:text-gray-400'>None</span>
              )}
            </dd>
          </div>

          <div>
            <dt className='font-semibold text-gray-600 dark:text-gray-400'>Post ID</dt>
            <dd className='text-sm break-all text-gray-900 dark:text-gray-100'>{post.id}</dd>
          </div>

          <div>
            <dt className='font-semibold text-gray-600 dark:text-gray-400'>User</dt>
            <dd className='text-sm break-all'>
              {user && (
                <div>
                  <Link
                    to={`/users/${post.user_id}`}
                    className='text-blue-600 hover:underline dark:text-blue-400 dark:hover:text-blue-300'
                  >
                    @{user.handle}
                  </Link>
                  <div className='mt-1 text-xs text-gray-500 dark:text-gray-500'>
                    DID: {user.did}
                  </div>
                </div>
              )}
              {!user && (
                <Link
                  to={`/users/${post.user_id}`}
                  className='text-blue-600 hover:underline dark:text-blue-400 dark:hover:text-blue-300'
                >
                  {post.user_id}
                </Link>
              )}
            </dd>
          </div>

          <div>
            <dt className='font-semibold text-gray-600 dark:text-gray-400'>Scheduled At</dt>
            <dd className='text-gray-900 dark:text-gray-100'>
              {formatDateTime(post.scheduled_at, { dateStyle: 'medium', timeStyle: 'short' }) ||
                'N/A'}
            </dd>
          </div>

          <div>
            <dt className='font-semibold text-gray-600 dark:text-gray-400'>Created At</dt>
            <dd className='text-gray-900 dark:text-gray-100'>
              {formatDateTime(post.created_at, { dateStyle: 'medium', timeStyle: 'short' }) ||
                'N/A'}
            </dd>
          </div>

          <div>
            <dt className='font-semibold text-gray-600 dark:text-gray-400'>Updated At</dt>
            <dd className='text-gray-900 dark:text-gray-100'>
              {formatDateTime(post.updated_at, { dateStyle: 'medium', timeStyle: 'short' }) ||
                'N/A'}
            </dd>
          </div>

          {post.executed_at && (
            <div>
              <dt className='font-semibold text-gray-600 dark:text-gray-400'>Executed At</dt>
              <dd className='text-gray-900 dark:text-gray-100'>
                {formatDateTime(post.executed_at, { dateStyle: 'medium', timeStyle: 'short' }) ||
                  'N/A'}
              </dd>
            </div>
          )}

          {post.at_uri && (
            <>
              <div className='md:col-span-2'>
                <dt className='font-semibold text-gray-600 dark:text-gray-400'>AT URI</dt>
                <dd className='text-sm break-all'>
                  <a
                    href={`https://bsky.app/profile/${user?.did}/post/${post.at_rkey}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-blue-600 hover:underline dark:text-blue-400 dark:hover:text-blue-300'
                  >
                    {post.at_uri}
                  </a>
                </dd>
              </div>

              {post.status === 'COMPLETED' && user?.handle && post.at_rkey && (
                <div className='md:col-span-2'>
                  <dt className='font-semibold text-gray-600 dark:text-gray-400'>
                    View on Bluesky
                  </dt>
                  <dd>
                    <a
                      href={`https://bsky.app/profile/${user.handle}/post/${post.at_rkey}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='inline-flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 font-semibold text-white transition-colors hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
                    >
                      <BlueskyIcon className='h-5 w-5' />
                      Open Post on Bluesky
                    </a>
                  </dd>
                </div>
              )}
            </>
          )}

          {post.error_msg && (
            <div className='md:col-span-2'>
              <dt className='font-semibold text-red-600 dark:text-red-400'>Error Message</dt>
              <dd className='mt-1 rounded bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950 dark:text-red-300'>
                {post.error_msg}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* ThreadGate and PostGate Settings */}
      {(post.threadgate_rules ||
        post.threadgate_uri ||
        post.postgate_disable_embedding !== null) && (
        <div className='mb-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800 dark:shadow-gray-900'>
          <h2 className='mb-4 text-xl font-semibold text-gray-900 dark:text-gray-100'>
            ThreadGate & PostGate Settings
          </h2>

          <dl className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            {/* ThreadGate Rules */}
            {post.threadgate_rules && post.threadgate_rules.length > 0 && (
              <div className='md:col-span-2'>
                <dt className='font-semibold text-gray-600 dark:text-gray-400'>
                  ThreadGate Rules (Reply Restrictions)
                </dt>
                <dd className='mt-2'>
                  <div className='flex flex-wrap gap-2'>
                    {post.threadgate_rules.map((rule, index) => (
                      <span
                        key={index}
                        className='inline-block rounded bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                      >
                        {threadgateRuleLabel(rule)}
                      </span>
                    ))}
                  </div>
                  <div className='mt-2 text-xs text-gray-500 dark:text-gray-400'>
                    Only specified users can reply to this post
                  </div>
                </dd>
              </div>
            )}

            {/* ThreadGate URI */}
            {post.threadgate_uri && (
              <div className='md:col-span-2'>
                <dt className='font-semibold text-gray-600 dark:text-gray-400'>ThreadGate URI</dt>
                <dd className='text-sm break-all text-gray-900 dark:text-gray-100'>
                  {post.threadgate_uri}
                </dd>
              </div>
            )}

            {/* ThreadGate CID */}
            {post.threadgate_cid && (
              <div>
                <dt className='font-semibold text-gray-600 dark:text-gray-400'>ThreadGate CID</dt>
                <dd className='text-sm break-all text-gray-900 dark:text-gray-100'>
                  {post.threadgate_cid}
                </dd>
              </div>
            )}

            {/* PostGate - Disable Embedding */}
            {post.postgate_disable_embedding !== null && (
              <div className='md:col-span-2'>
                <dt className='font-semibold text-gray-600 dark:text-gray-400'>
                  PostGate - Quote Post Disabled
                </dt>
                <dd>
                  <span
                    className={`inline-block rounded px-3 py-1 text-sm font-semibold ${
                      post.postgate_disable_embedding
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    }`}
                  >
                    {post.postgate_disable_embedding ? 'Disabled' : 'Enabled'}
                  </span>
                  {post.postgate_disable_embedding && (
                    <div className='mt-2 text-xs text-gray-500 dark:text-gray-400'>
                      Other users cannot quote this post
                    </div>
                  )}
                </dd>
              </div>
            )}

            {/* PostGate URI */}
            {post.postgate_uri && (
              <div className='md:col-span-2'>
                <dt className='font-semibold text-gray-600 dark:text-gray-400'>PostGate URI</dt>
                <dd className='text-sm break-all text-gray-900 dark:text-gray-100'>
                  {post.postgate_uri}
                </dd>
              </div>
            )}

            {/* PostGate CID */}
            {post.postgate_cid && (
              <div>
                <dt className='font-semibold text-gray-600 dark:text-gray-400'>PostGate CID</dt>
                <dd className='text-sm break-all text-gray-900 dark:text-gray-100'>
                  {post.postgate_cid}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {/* Raw Content Details */}
      <div className='mb-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800 dark:shadow-gray-900'>
        <h2 className='mb-4 text-xl font-semibold text-gray-900 dark:text-gray-100'>Raw Content</h2>
        <div className='rounded-lg bg-gray-50 p-4 dark:bg-gray-700'>
          <pre className='font-mono text-sm break-words whitespace-pre-wrap text-gray-900 dark:text-gray-100'>
            {post.content}
          </pre>
        </div>
        <div className='mt-2 text-xs text-gray-500 dark:text-gray-400'>
          Length: {post.content.length} characters | Byte length: {new Blob([post.content]).size}{' '}
          bytes
        </div>
      </div>

      {/* Facets Details */}
      {Array.isArray(post.facets) && (post.facets as any[]).length > 0 && (
        <div className='mb-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800 dark:shadow-gray-900'>
          <h2 className='mb-4 text-xl font-semibold text-gray-900 dark:text-gray-100'>
            Facets Details ({(post.facets as any[]).length})
          </h2>
          <div className='space-y-4'>
            {(post.facets as any[]).map((facet: any, index: number) => (
              <div
                key={index}
                className='rounded-lg border border-gray-200 p-4 dark:border-gray-600'
              >
                <div className='mb-2 flex items-center justify-between'>
                  <h3 className='font-semibold text-gray-900 dark:text-gray-100'>
                    Facet #{index + 1}
                  </h3>
                  <span className='rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200'>
                    {facet.$type}
                  </span>
                </div>

                {/* Index Information */}
                <div className='mb-3 grid grid-cols-2 gap-2 text-sm'>
                  <div>
                    <span className='font-semibold text-gray-600 dark:text-gray-400'>
                      Byte Start:
                    </span>{' '}
                    <span className='text-gray-900 dark:text-gray-100'>
                      {facet.index?.byteStart}
                    </span>
                  </div>
                  <div>
                    <span className='font-semibold text-gray-600 dark:text-gray-400'>
                      Byte End:
                    </span>{' '}
                    <span className='text-gray-900 dark:text-gray-100'>{facet.index?.byteEnd}</span>
                  </div>
                </div>

                {/* Extract text using byte indices */}
                {facet.index?.byteStart != null && facet.index?.byteEnd != null && (
                  <div className='mb-3'>
                    <span className='text-sm font-semibold text-gray-600 dark:text-gray-400'>
                      Extracted Text:
                    </span>
                    <div className='mt-1 rounded bg-yellow-50 px-3 py-2 dark:bg-yellow-900/20'>
                      <code className='text-sm text-gray-900 dark:text-gray-100'>
                        {decodeFacetText(post.content, facet)}
                      </code>
                    </div>
                  </div>
                )}

                {/* Features */}
                {facet.features && Array.isArray(facet.features) && facet.features.length > 0 && (
                  <div>
                    <h4 className='mb-2 text-sm font-semibold text-gray-600 dark:text-gray-400'>
                      Features ({facet.features.length}):
                    </h4>
                    <div className='space-y-2'>
                      {facet.features.map((feature: any, fIndex: number) => (
                        <div key={fIndex} className='rounded bg-gray-50 p-3 dark:bg-gray-700'>
                          <div className='mb-2'>
                            <span className='rounded bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800 dark:bg-purple-900 dark:text-purple-200'>
                              {feature.$type}
                            </span>
                          </div>

                          {/* Mention Feature */}
                          {feature.$type === 'app.bsky.richtext.facet#mention' && (
                            <div>
                              <div className='text-sm'>
                                <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                  DID:
                                </span>{' '}
                                <code
                                  className={`rounded px-1 ${
                                    feature.did?.startsWith('did:')
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  }`}
                                >
                                  {feature.did}
                                </code>
                              </div>
                              {!feature.did?.startsWith('did:') && (
                                <div className='mt-2 flex items-start gap-2 rounded bg-red-50 p-2 dark:bg-red-950'>
                                  <svg
                                    className='h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                  >
                                    <path
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                      strokeWidth={2}
                                      d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                                    />
                                  </svg>
                                  <div className='text-xs text-red-700 dark:text-red-300'>
                                    <strong>Invalid DID format!</strong> This should be a DID (e.g.,
                                    did:plc:xxx), not a handle. This will cause posting to fail.
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Link Feature */}
                          {feature.$type === 'app.bsky.richtext.facet#link' && (
                            <div className='text-sm'>
                              <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                URI:
                              </span>{' '}
                              <a
                                href={feature.uri}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-blue-600 hover:underline dark:text-blue-400'
                              >
                                {feature.uri}
                              </a>
                            </div>
                          )}

                          {/* Tag Feature */}
                          {feature.$type === 'app.bsky.richtext.facet#tag' && (
                            <div className='text-sm'>
                              <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                Tag:
                              </span>{' '}
                              <code className='rounded bg-blue-100 px-1 text-blue-800 dark:bg-blue-900 dark:text-blue-200'>
                                #{feature.tag}
                              </code>
                            </div>
                          )}

                          {/* Unknown Feature - show raw JSON */}
                          {![
                            'app.bsky.richtext.facet#mention',
                            'app.bsky.richtext.facet#link',
                            'app.bsky.richtext.facet#tag'
                          ].includes(feature.$type) && (
                            <div className='text-sm'>
                              <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                Raw Data:
                              </span>
                              <pre className='mt-1 overflow-auto text-xs text-gray-900 dark:text-gray-100'>
                                {JSON.stringify(feature, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Raw JSON */}
          <div className='mt-4'>
            <button
              onClick={() => setShowDebugInfo(!showDebugInfo)}
              className='text-sm text-blue-600 hover:underline dark:text-blue-400 dark:hover:text-blue-300'
            >
              {showDebugInfo ? 'Hide' : 'Show'} Raw JSON
            </button>
            {showDebugInfo && (
              <pre className='mt-2 max-h-60 overflow-auto rounded bg-gray-50 p-3 text-xs text-gray-900 dark:bg-gray-700 dark:text-gray-100'>
                {JSON.stringify(post.facets, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* Embed Details */}
      {post.embed != null && (
        <div className='mb-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800 dark:shadow-gray-900'>
          <h2 className='mb-4 text-xl font-semibold text-gray-900 dark:text-gray-100'>
            Embed Details
          </h2>
          <div className='rounded-lg border border-gray-200 p-4 dark:border-gray-600'>
            <div className='mb-3'>
              <span className='rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200'>
                {isEmbedImages(post.embed)
                  ? 'app.bsky.embed.images'
                  : isEmbedExternal(post.embed)
                    ? 'app.bsky.embed.external'
                    : isEmbedVideo(post.embed)
                      ? 'app.bsky.embed.video'
                      : 'unknown'}
              </span>
            </div>

            {/* Images Embed */}
            {isEmbedImages(post.embed) && (
              <div>
                <h3 className='mb-2 font-semibold text-gray-900 dark:text-gray-100'>
                  Images ({post.embed.images.length})
                </h3>
                <div className='space-y-3'>
                  {post.embed.images.map((image, index) => (
                    <div key={index} className='rounded bg-gray-50 p-3 dark:bg-gray-700'>
                      <div className='mb-2 font-semibold text-gray-900 dark:text-gray-100'>
                        Image #{index + 1}
                      </div>
                      <div className='space-y-2 text-sm'>
                        {image.image && (
                          <>
                            {image.image.ref && (
                              <div>
                                <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                  CID:
                                </span>{' '}
                                <code className='break-all text-gray-900 dark:text-gray-100'>
                                  {getBlobCid(image.image.ref)}
                                </code>
                              </div>
                            )}
                            {image.image.mimeType && (
                              <div>
                                <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                  MIME Type:
                                </span>{' '}
                                <span className='text-gray-900 dark:text-gray-100'>
                                  {image.image.mimeType}
                                </span>
                              </div>
                            )}
                            {image.image.size != null && (
                              <div>
                                <span className='font-semibold text-gray-600 dark:text-gray-400'>
                                  Size:
                                </span>{' '}
                                <span className='text-gray-900 dark:text-gray-100'>
                                  {(image.image.size / 1024).toFixed(2)} KB
                                </span>
                              </div>
                            )}
                          </>
                        )}
                        {image.alt && (
                          <div>
                            <span className='font-semibold text-gray-600 dark:text-gray-400'>
                              Alt Text:
                            </span>
                            <div className='mt-1 rounded bg-yellow-50 p-2 dark:bg-yellow-900/20'>
                              <span className='text-gray-900 dark:text-gray-100'>{image.alt}</span>
                            </div>
                          </div>
                        )}
                        {image.aspectRatio && (
                          <div>
                            <span className='font-semibold text-gray-600 dark:text-gray-400'>
                              Aspect Ratio:
                            </span>{' '}
                            <span className='text-gray-900 dark:text-gray-100'>
                              {image.aspectRatio.width} : {image.aspectRatio.height}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* External Embed */}
            {isEmbedExternal(post.embed) && (
              <div>
                <h3 className='mb-2 font-semibold text-gray-900 dark:text-gray-100'>
                  External Link
                </h3>
                <div className='space-y-2 text-sm'>
                  <div>
                    <span className='font-semibold text-gray-600 dark:text-gray-400'>URI:</span>{' '}
                    <a
                      href={isSafeUri(post.embed.external.uri) ? post.embed.external.uri : '#'}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-blue-600 hover:underline dark:text-blue-400'
                    >
                      {post.embed.external.uri}
                    </a>
                  </div>
                  {post.embed.external.title && (
                    <div>
                      <span className='font-semibold text-gray-600 dark:text-gray-400'>Title:</span>{' '}
                      <span className='text-gray-900 dark:text-gray-100'>
                        {post.embed.external.title}
                      </span>
                    </div>
                  )}
                  {post.embed.external.description && (
                    <div>
                      <span className='font-semibold text-gray-600 dark:text-gray-400'>
                        Description:
                      </span>
                      <div className='mt-1 text-gray-900 dark:text-gray-100'>
                        {post.embed.external.description}
                      </div>
                    </div>
                  )}
                  {post.embed.external.thumb && (
                    <div>
                      <span className='font-semibold text-gray-600 dark:text-gray-400'>
                        Thumbnail:
                      </span>
                      <div className='mt-1 space-y-1'>
                        {post.embed.external.thumb.ref && (
                          <div>
                            <span className='text-xs text-gray-600 dark:text-gray-400'>CID:</span>{' '}
                            <code className='text-xs break-all text-gray-900 dark:text-gray-100'>
                              {getBlobCid(post.embed.external.thumb.ref)}
                            </code>
                          </div>
                        )}
                        {post.embed.external.thumb.mimeType && (
                          <div className='text-xs text-gray-600 dark:text-gray-400'>
                            MIME: {post.embed.external.thumb.mimeType}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Raw JSON */}
            <div className='mt-4'>
              <details>
                <summary className='cursor-pointer text-sm text-blue-600 hover:underline dark:text-blue-400 dark:hover:text-blue-300'>
                  Show Raw JSON
                </summary>
                <pre className='mt-2 max-h-60 overflow-auto rounded bg-gray-50 p-3 text-xs text-gray-900 dark:bg-gray-700 dark:text-gray-100'>
                  {JSON.stringify(post.embed, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Actions */}
      {canChangeStatus && (
        <div className='mb-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800 dark:shadow-gray-900'>
          <h2 className='mb-4 text-xl font-semibold text-gray-900 dark:text-gray-100'>
            Change Status
          </h2>
          <div className='flex flex-wrap gap-2'>
            {availableStatuses.map(status => (
              <button
                key={status}
                onClick={() => updateStatus(status)}
                disabled={updating}
                className={`rounded px-4 py-2 font-semibold text-white transition-colors ${
                  updating
                    ? 'cursor-not-allowed bg-gray-400 dark:bg-gray-600'
                    : status === 'CANCELLED'
                      ? 'bg-gray-500 hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-700'
                      : status === 'FAILED'
                        ? 'bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700'
                        : status === 'COMPLETED'
                          ? 'bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700'
                          : 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
                }`}
              >
                Change to {status}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Delete Action */}
      <div className='rounded-lg bg-white p-6 shadow dark:bg-gray-800 dark:shadow-gray-900'>
        <h2 className='mb-4 text-xl font-semibold text-red-600 dark:text-red-400'>Danger Zone</h2>
        <button
          onClick={deletePost}
          disabled={updating}
          className='rounded bg-red-600 px-4 py-2 font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400 dark:bg-red-700 dark:hover:bg-red-800 dark:disabled:bg-gray-600'
        >
          Delete Post
        </button>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText='Confirm'
        cancelText='Cancel'
        onConfirm={confirmModal.onConfirm}
        onCancel={() =>
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })
        }
        variant={confirmModal.variant}
      />

      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        buttonText='OK'
        onClose={() => setAlertModal({ isOpen: false, title: '', message: '' })}
        variant={alertModal.variant}
      />

      <ImageLightbox
        isOpen={lightbox.isOpen}
        images={lightbox.images}
        initialIndex={lightbox.initialIndex}
        onClose={() => setLightbox({ isOpen: false, images: [], initialIndex: 0 })}
      />
    </div>
  );
}
